/**
 * IoT Cloud Services Configuration - Zero-Trust FQDN & Port Matrix
 * 
 * This module defines authorized cloud endpoints and ports for IoT/Smart Home devices.
 * Based on deep research into cloud ecosystems from:
 * - Hikvision, Dahua (CCTV)
 * - Amazon Alexa, Google Nest (Voice Assistants)
 * - Shelly, Tuya, Sonoff (Smart Home Automation)
 * - Sonos (Audio)
 * - Samsung, LG (Smart TVs)
 */

// ============================================
// FQDN Address Objects for GRP_FQDN_IOT
// ============================================
export const IOT_FQDN_GROUPS = {
  // Universal Time Services
  ntp: [
    '*.pool.ntp.org',
    'time.google.com',
    'time.windows.com',
    'time.apple.com',
  ],
  
  // Hikvision / EZVIZ CCTV
  hikvision: [
    '*.hik-connect.com',
    '*.ezviz.com',
    '*.ezvizlife.com',
    '*.hikvision.com',
  ],
  
  // Dahua CCTV
  dahua: [
    '*.easy4ipcloud.com',
    '*.easy4ip.com',
    '*.dahuasecurity.com',
  ],
  
  // Amazon Alexa
  alexa: [
    '*.amazon.com',
    'pins.amazon.com',
    '*.amazonalexa.com',
    '*.media-amazon.com',
    'avs-alexa-*.amazon.com',
  ],
  
  // Google Nest / Home
  google_nest: [
    '*.google.com',
    'clients3.google.com',
    '*.gstatic.com',
    '*.googleapis.com',
    'accounts.google.com',
  ],
  
  // Shelly Smart Home
  shelly: [
    '*.shelly.cloud',
    'notifications.allterco.com',
    '*.shelly.com',
  ],
  
  // Tuya / Smart Life
  tuya: [
    '*.tuyaus.com',
    '*.tuyaeu.com',
    '*.tuyacn.com',
    '*.iotbing.com',
    '*.smartlife.com',
  ],
  
  // Sonoff / eWeLink
  sonoff: [
    '*.itead.cn',
    '*.coolkit.cc',
    '*.ewelink.cc',
  ],
  
  // Sonos Audio
  sonos: [
    '*.sonos.com',
    '*.ws.sonos.com',
    '*.sslauth.sonos.com',
  ],
  
  // Smart TVs (Samsung, LG)
  smart_tvs: [
    '*.samsungcloudsolution.com',
    '*.samsungotn.net',
    '*.samsung.com',
    '*.lgtvonline.lge.com',
    '*.lgappstv.com',
    '*.lge.com',
  ],
};

// All FQDNs flattened for group creation
export const ALL_IOT_FQDNS: string[] = Object.values(IOT_FQDN_GROUPS).flat();

// ============================================
// Service Objects / Ports for GRP_SRV_IOT
// ============================================
export const IOT_PORTS = {
  tcp: [
    53,      // DNS
    80,      // HTTP
    443,     // HTTPS
    1400,    // Sonos HTTP
    1443,    // Alternate HTTPS
    2443,    // Tuya SSL
    3400,    // Various IoT
    4070,    // Spotify Connect
    6010, 6011, 6012, 6013, 6014, 6015, 6016, 6017, 6018, 6019, 6020, 6021, 6022, // Hikvision range
    6110,    // HP JetDirect
    6113,    // Various IoT
    8000,    // Hikvision HTTP
    8008,    // Google Cast HTTP
    8009,    // Google Cast
    8080,    // Alternative HTTP
    8800, 8801, 8802, 8803, // Dahua range
    8883,    // MQTT SSL
    8886,    // MQTT Alt
    8900, 8901, 8902, 8903, // Dahua range
    9010,    // Various IoT
    9020,    // Various IoT
    9543,    // Tuya
    10443,   // Various IoT SSL
    11095,   // Hikvision
    12366,   // Various IoT
    33434,   // Traceroute
    37777,   // Dahua TCP
    40317,   // Various IoT
    49317,   // Various IoT
  ],
  udp: [
    53,      // DNS
    123,     // NTP
    1900,    // SSDP/UPnP
    3478,    // STUN
    5353,    // mDNS
    5683,    // CoAP
    6666,    // Various IoT
    6667,    // Various IoT
    6969,    // BitTorrent DHT
    8800, 8801, 8802, 8803, // Dahua range
    10293,   // Various IoT
    10294,   // Various IoT
    33434,   // Traceroute
    37778,   // Dahua UDP
    40317,   // Various IoT
    49317,   // Various IoT
    // Dynamic/Ephemeral range for RTP, WebRTC, etc.
    // 49152-65535 (handled separately in generators)
  ],
};

// TCP Ports as comma-separated string
export const IOT_TCP_PORTS_STRING = IOT_PORTS.tcp.join(',');

// UDP Ports as comma-separated string (without ephemeral range)
export const IOT_UDP_PORTS_STRING = IOT_PORTS.udp.join(',');

// TCP Ports with ranges for efficiency
export const IOT_TCP_PORT_RANGES = [
  '53',
  '80',
  '443',
  '1400',
  '1443',
  '2443',
  '3400',
  '4070',
  '6010-6022',
  '6110',
  '6113',
  '8000',
  '8008-8009',
  '8080',
  '8800-8803',
  '8883',
  '8886',
  '8900-8903',
  '9010',
  '9020',
  '9543',
  '10443',
  '11095',
  '12366',
  '33434',
  '37777',
  '40317',
  '49317',
];

export const IOT_UDP_PORT_RANGES = [
  '53',
  '123',
  '1900',
  '3478',
  '5353',
  '5683',
  '6666-6667',
  '6969',
  '8800-8803',
  '10293-10294',
  '33434',
  '37778',
  '40317',
  '49317',
  '49152-65535', // Ephemeral/dynamic range
];

// ============================================
// Helper function to get IoT VLAN info
// ============================================
export interface IoTVlanInfo {
  vlan_id: string;
  nome_vlan: string;
  network_ip: string;
  gateway_ip: string;
  subnet_mask: string;
}

/**
 * Extract IoT VLANs from the VLAN list
 */
export const getIoTVlans = (vlans: any[]): IoTVlanInfo[] => {
  return vlans.filter(v => v.vlan_type === 'iot').map(v => ({
    vlan_id: v.vlan_id,
    nome_vlan: v.nome_vlan,
    network_ip: v.network_ip,
    gateway_ip: v.gateway_ip,
    subnet_mask: v.subnet_mask,
  }));
};

/**
 * Get network address with CIDR notation from IP and subnet mask
 */
export const getNetworkWithCIDR = (ip: string, mask: string): string => {
  const cidrMap: Record<string, number> = {
    '255.255.255.255': 32,
    '255.255.255.252': 30,
    '255.255.255.248': 29,
    '255.255.255.240': 28,
    '255.255.255.224': 27,
    '255.255.255.192': 26,
    '255.255.255.128': 25,
    '255.255.255.0': 24,
    '255.255.254.0': 23,
    '255.255.252.0': 22,
    '255.255.248.0': 21,
    '255.255.240.0': 20,
    '255.255.0.0': 16,
    '255.0.0.0': 8,
  };
  const cidr = cidrMap[mask] || 24;
  return `${ip}/${cidr}`;
};
